import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { worksheetService, type Worksheet } from "@/services/worksheet.service";
import {
  ArrowLeft,
  Printer,
  Camera,
  FileText,
  ChevronDown,
  AlertCircle,
  ListChecks,
} from "lucide-react";
import { PdfViewer } from "@/components/worksheets/PdfViewer";

function isRequestCancelled(error: unknown): boolean {
  // Handle both DOMException AbortError and Axios CanceledError
  if (error instanceof DOMException && error.name === "AbortError") {
    return true;
  }
  // Axios wraps abort as a different error
  if (error && typeof error === "object") {
    const err = error as { code?: string; name?: string };
    if (err.code === "ERR_CANCELED" || err.name === "CanceledError") {
      return true;
    }
  }
  return false;
}

// Loading skeleton for the page
function WorksheetDetailSkeleton() {
  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header skeleton */}
      <div className="space-y-4">
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-9 w-64" />
        <div className="flex gap-2">
          <Skeleton className="h-6 w-20" />
          <Skeleton className="h-6 w-24" />
          <Skeleton className="h-6 w-16" />
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* PDF preview skeleton */}
        <div className="lg:col-span-2">
          <Skeleton className="h-[600px] w-full rounded-lg" />
        </div>

        {/* Info card skeleton */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-40" />
            </CardHeader>
            <CardContent className="space-y-4">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
            </CardContent>
          </Card>

          <div className="space-y-3">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        </div>
      </div>
    </div>
  );
}

// Error state component
function WorksheetDetailError({ error, onRetry }: { error: string; onRetry: () => void }) {
  return (
    <div className="container mx-auto p-6">
      <Link
        to="/worksheets"
        className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-6"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to Worksheets
      </Link>

      <Card className="max-w-md mx-auto">
        <CardHeader>
          <div className="flex items-center gap-2 text-destructive">
            <AlertCircle className="h-5 w-5" />
            <CardTitle>Failed to Load Worksheet</CardTitle>
          </div>
          <CardDescription>{error}</CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={onRetry} className="w-full">
            Try Again
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

// Get question type badge styles
function getQuestionTypeBadge(type: string): { label: string; variant: "default" | "secondary" | "outline" } {
  switch (type) {
    case "circle":
      return { label: "Circle", variant: "default" };
    case "write":
      return { label: "Write", variant: "secondary" };
    case "draw":
      return { label: "Draw", variant: "outline" };
    case "count":
      return { label: "Count", variant: "default" };
    case "compare":
      return { label: "Compare", variant: "secondary" };
    default:
      return { label: type, variant: "outline" };
  }
}

// Count question types
function getQuestionTypeBreakdown(questions: Worksheet["questions"]): Record<string, number> {
  if (!questions) return {};

  const breakdown: Record<string, number> = {};
  for (const q of questions) {
    breakdown[q.type] = (breakdown[q.type] || 0) + 1;
  }
  return breakdown;
}

export default function WorksheetDetail() {
  const { worksheetId } = useParams<{ worksheetId: string }>();
  const [worksheet, setWorksheet] = useState<Worksheet | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [questionsOpen, setQuestionsOpen] = useState(false);
  const retryControllerRef = useRef<AbortController | null>(null);

  const fetchWorksheet = useCallback(async (signal?: AbortSignal) => {
    if (!worksheetId) {
      setError("No worksheet ID provided");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await worksheetService.getWorksheet(worksheetId, signal);
      if (!signal?.aborted) {
        setWorksheet(data);
      }
    } catch (err) {
      if (!isRequestCancelled(err)) {
        console.error("Failed to load worksheet:", err);
        setError(
          err instanceof Error ? err.message : "Failed to load worksheet. Please try again."
        );
      }
    } finally {
      if (!signal?.aborted) {
        setLoading(false);
      }
    }
  }, [worksheetId]);

  useEffect(() => {
    const controller = new AbortController();
    fetchWorksheet(controller.signal);
    return () => {
      controller.abort();
      // Also abort any pending retry
      retryControllerRef.current?.abort();
    };
  }, [fetchWorksheet]);

  const handleRetry = () => {
    // Abort previous retry if any
    retryControllerRef.current?.abort();
    const controller = new AbortController();
    retryControllerRef.current = controller;
    fetchWorksheet(controller.signal);
  };

  const handlePrint = () => {
    if (worksheetId) {
      const pdfUrl = worksheetService.getWorksheetPdfUrl(worksheetId);
      window.open(pdfUrl, "_blank");
    }
  };

  if (loading) {
    return <WorksheetDetailSkeleton />;
  }

  if (error) {
    return <WorksheetDetailError error={error} onRetry={handleRetry} />;
  }

  if (!worksheet) {
    return <WorksheetDetailError error="Worksheet not found" onRetry={handleRetry} />;
  }

  const questionTypeBreakdown = getQuestionTypeBreakdown(worksheet.questions);
  const questionCount = worksheet.questions?.length || 0;
  const pdfUrl = worksheetService.getWorksheetPdfUrl(worksheet.id);

  // Format subject name for display
  const formatSubject = (subject: string) => {
    return subject
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header Section */}
      <div className="space-y-4">
        <Link
          to="/worksheets"
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Worksheets
        </Link>

        <div className="space-y-2">
          <h1 className="text-3xl font-bold tracking-tight">{worksheet.title}</h1>

          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="secondary">Grade {worksheet.grade}</Badge>
            <Badge variant="outline">{formatSubject(worksheet.subject)}</Badge>
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* PDF Preview */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <FileText className="h-5 w-5" />
                Worksheet Preview
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <PdfViewer url={pdfUrl} className="h-[600px] rounded-b-lg overflow-hidden" />
            </CardContent>
          </Card>
        </div>

        {/* Info Panel */}
        <div className="space-y-6">
          {/* Worksheet Info Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <ListChecks className="h-5 w-5" />
                Worksheet Info
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Curriculum Outcomes */}
              {worksheet.outcome_ids && worksheet.outcome_ids.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-2">
                    Curriculum Outcomes
                  </h4>
                  <ul className="space-y-1">
                    {worksheet.outcome_ids.map((outcomeId) => (
                      <li key={outcomeId} className="text-sm">
                        <Badge variant="outline" className="font-mono">
                          {outcomeId}
                        </Badge>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Question Count */}
              <div>
                <h4 className="text-sm font-medium text-muted-foreground mb-1">
                  Questions
                </h4>
                <p className="text-2xl font-bold">{questionCount}</p>
              </div>

              {/* Question Types Breakdown */}
              {Object.keys(questionTypeBreakdown).length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-2">
                    Question Types
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(questionTypeBreakdown).map(([type, count]) => {
                      const badge = getQuestionTypeBadge(type);
                      return (
                        <Badge key={type} variant={badge.variant}>
                          {badge.label}: {count}
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="space-y-3">
            <Button className="w-full" size="lg" onClick={handlePrint}>
              <Printer className="mr-2 h-5 w-5" />
              Print Worksheet
            </Button>

            <Button variant="outline" className="w-full" size="lg" asChild>
              <Link to={`/scan?worksheet_id=${worksheet.id}`}>
                <Camera className="mr-2 h-5 w-5" />
                Scan Completed
              </Link>
            </Button>
          </div>

          {/* Questions Preview (Collapsible) */}
          {worksheet.questions && worksheet.questions.length > 0 && (
            <Collapsible open={questionsOpen} onOpenChange={setQuestionsOpen}>
              <Card>
                <CollapsibleTrigger asChild>
                  <Button
                    variant="ghost"
                    className="w-full justify-between p-4 h-auto"
                  >
                    <span className="font-semibold">Questions Preview</span>
                    <ChevronDown
                      className={`h-5 w-5 transition-transform ${
                        questionsOpen ? "rotate-180" : ""
                      }`}
                    />
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="pt-0">
                    <ul className="space-y-3">
                      {worksheet.questions.map((question, index) => {
                        const badge = getQuestionTypeBadge(question.type);
                        return (
                          <li
                            key={question.id}
                            className="flex items-start gap-3 pb-3 border-b last:border-0 last:pb-0"
                          >
                            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-muted flex items-center justify-center text-xs font-medium">
                              {index + 1}
                            </span>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm">{question.prompt}</p>
                              <Badge
                                variant={badge.variant}
                                className="mt-1 text-xs"
                              >
                                {badge.label}
                              </Badge>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>
          )}
        </div>
      </div>
    </div>
  );
}
